<template>
    <div class="form-wrapper">
        <h1>Export Submissions</h1>
        <p>Choose which submissions to export</p>

        <div class="alert alert-danger p-0" v-if="error">{{ error }}</div>

        <form action="#" @submit.prevent="exportCSV" class="new-project-form">
            <div class="fields-wrapper">

                <!-- Period -->
                <div class="form-group">
                    <label>Period</label>
                    <select class="form-control" v-model="period">
                        <option value="all">All submissions</option>
                        <option value="monthly">Monthly</option>
                        <option value="annually">Annually</option>
                        <option value="custom">Custom range</option>
                    </select>
                </div>

                <!-- Monthly selector -->
                <div class="row" v-if="period === 'monthly'">
                    <div class="col-8">
                        <div class="form-group">
                            <label>Month</label>
                            <select class="form-control" v-model="month" :disabled="loadingPeriods">
                                <option value="" disabled>{{ loadingPeriods ? 'Loading...' : 'Select a month' }}</option>
                                <option v-for="m in availableMonths" :key="m.value" :value="m.value">{{ m.label }}</option>
                            </select>
                        </div>
                    </div>
                </div>

                <!-- Annually selector -->
                <div class="row" v-if="period === 'annually'">
                    <div class="col-8">
                        <div class="form-group">
                            <label>Year</label>
                            <select class="form-control" v-model="year" :disabled="loadingPeriods">
                                <option value="" disabled>{{ loadingPeriods ? 'Loading...' : 'Select a year' }}</option>
                                <option v-for="y in availableYears" :key="y.value" :value="y.value">{{ y.label }}</option>
                            </select>
                        </div>
                    </div>
                </div>

                <!-- Custom range -->
                <div class="row" v-if="period === 'custom'">
                    <div class="col-6">
                        <div class="form-group">
                            <label>Start Date</label>
                            <input type="date" class="form-control" v-model="startDate">
                        </div>
                    </div>
                    <div class="col-6">
                        <div class="form-group">
                            <label>End Date</label>
                            <input type="date" class="form-control" v-model="endDate">
                        </div>
                    </div>
                </div>

                <!-- Filters -->
                <div class="export-filters">
                    <label class="filters-label">Filters</label>
                    <div class="custom-control custom-checkbox">
                        <input class="custom-control-input" type="checkbox" v-model="newOnly" id="export-new-only">
                        <label class="custom-control-label" for="export-new-only">New submissions only</label>
                    </div>
                    <div class="custom-control custom-checkbox">
                        <input class="custom-control-input" type="checkbox" v-model="includeSpam" id="export-include-spam">
                        <label class="custom-control-label" for="export-include-spam">Include spam</label>
                    </div>
                </div>

                <div class="mt-4">
                    <button type="submit" class="btn btn-primary" style="width: 180px" :disabled="exporting">
                        <span class="spinner-border" v-if="exporting"></span>
                        <span :class="{'opacity-0': exporting}">Export CSV</span>
                    </button>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import repository from "@/repository/repository";

export default {
    name: "ExportModal",
    props: ['formName'],
    data() {
        return {
            period: "all",
            month: "",
            year: "",
            startDate: "",
            endDate: "",
            newOnly: false,
            includeSpam: false,
            exporting: false,
            loadingPeriods: false,
            availableMonths: [],
            availableYears: [],
            periodsLoaded: false,
            error: null,
        }
    },
    methods: {
        loadPeriods() {
            if (this.periodsLoaded || this.loadingPeriods) return;
            this.loadingPeriods = true;
            repository.get("/projects/" + this.projectId + "/forms/" + this.formId + "/submissions/periods")
                .then(response => {
                    this.availableMonths = response.data.months;
                    this.availableYears = response.data.years;
                    this.periodsLoaded = true;
                    this.loadingPeriods = false;
                })
                .catch(error => {
                    console.log(error);
                    this.loadingPeriods = false;
                })
        },
        exportCSV() {
            if (this.exporting) return;
            this.error = null;

            // Validation
            if (this.period === 'monthly' && !this.month) {
                this.error = "Please select a month.";
                return;
            }
            if (this.period === 'annually' && !this.year) {
                this.error = "Please select a year.";
                return;
            }
            if (this.period === 'custom') {
                if (!this.startDate || !this.endDate) {
                    this.error = "Both start date and end date are required.";
                    return;
                }
                if (this.startDate > this.endDate) {
                    this.error = "Start date must be before or equal to end date.";
                    return;
                }
            }

            this.exporting = true;

            let params = { period: this.period };
            if (this.period === 'monthly') params.month = this.month;
            if (this.period === 'annually') params.year = this.year;
            if (this.period === 'custom') {
                params.start_date = this.startDate;
                params.end_date = this.endDate;
            }
            if (this.newOnly) params.status = 'new';
            if (this.includeSpam) params.include_spam = 1;

            repository.get("/projects/" + this.projectId + "/forms/" + this.formId + "/submissions/export", {
                params: params,
                timeout: 0,
            })
                .then(response => {
                    const blob = new Blob([response.data], { type: 'text/csv' });
                    const link = document.createElement('a');
                    link.href = URL.createObjectURL(blob);
                    link.download = this.formName + " Submissions.csv";
                    link.click();
                    URL.revokeObjectURL(link.href);
                    this.exporting = false;
                    this.$emit('close');
                })
                .catch(error => {
                    console.log(error);
                    this.error = "An error occurred while exporting submissions.";
                    this.exporting = false;
                })
        },
        reset() {
            this.period = "all";
            this.month = "";
            this.year = "";
            this.startDate = "";
            this.endDate = "";
            this.newOnly = false;
            this.includeSpam = false;
            this.exporting = false;
            this.error = null;
        },
    },
    computed: {
        formId() {
            return this.$route.params.id;
        },
        projectId() {
            return this.$store.getters.currentProject.hashId;
        },
    },
    watch: {
        period(val) {
            if (val === 'monthly' || val === 'annually') {
                this.loadPeriods();
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../scss/variables";

.export-filters {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid $border-grey;

    .filters-label {
        display: block;
        font-size: 1rem;
        font-weight: 600;
        margin-bottom: 0.5rem;
        color: $dark;
    }

    .custom-control.custom-checkbox {
        padding-left: 0;
        font-size: 1rem;
        color: $grey-text;
        margin: 0 0 0.25rem 0;
        cursor: pointer;
        @extend .disable-selection;

        .custom-control-label {
            padding: 0 0 0 2rem;
            font-weight: 400;
            color: $dark;
            cursor: pointer;
            display: block;
            width: 100%;
            flex-shrink: 0;
            font-size: 0.9rem;
            margin: 0;

            &:before {
                top: 0;
                bottom: 0;
                margin: auto;
                left: 0;
                width: 1.5rem;
                height: 1.5rem;
                border-color: $border-grey;
            }

            &:after {
                top: 0;
                bottom: 0;
                margin: auto;
                width: 1.5rem;
                height: 1.5rem;
                left: 0;
            }
        }

        .custom-control-input ~ .custom-control-label::before {
            border: 1px solid $border-dark-grey;
        }

        .custom-control-input:focus ~ .custom-control-label::before {
            box-shadow: rgba($primary, 0.15) 0 0 0 0.2rem;
        }

        .custom-control-input:not(:disabled):active ~ .custom-control-label::before {
            background: rgba($primary, 0.15);
            border-color: rgba($primary, 0.15);
        }

        .custom-control-input:checked ~ .custom-control-label::before {
            background: $primary;
            border-color: $primary;
        }
    }
}
</style>
